import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/**
 * Shared visual shell for every major Inspection page section, so the whole
 * page reads as one connected workflow (Inspection → Findings → Operations →
 * Report → History).
 *
 * A single consistent header treatment: theme dark bar (`bg-primary`, so it
 * follows the active tenant/field theme via CSS variables — olive by default,
 * ocean-blue under `.theme-field`), a Font Awesome icon, a white title, a fixed
 * min height and consistent padding. Action buttons passed via `actions` are
 * restyled centrally to a translucent light treatment so they stay legible on
 * the dark bar without touching each call site.
 *
 * Presentation only — no business logic, permissions or data live here.
 */

// Header action buttons sit on the dark bar. Secondary actions get a uniform
// translucent-light outline; a button marked `is-primary` gets a solid light
// chip so primary actions (Approve, Add Finding, Send…) clearly out-rank
// secondary ones (Return, Reject). Descendant selectors win over each Button's
// own variant classes; `button.is-primary` is more specific than `button`, so
// the primary treatment wins in turn. A visible focus ring is included for
// keyboard users on the dark ground.
const HEADER_ACTION_STYLES =
  // secondary (default)
  '[&_button]:h-8 [&_button]:rounded-md [&_button]:border [&_button]:border-primary-foreground/30 ' +
  '[&_button]:bg-primary-foreground/10 [&_button]:px-3 [&_button]:text-xs [&_button]:font-medium ' +
  '[&_button]:text-primary-foreground [&_button:hover]:bg-primary-foreground/20 ' +
  '[&_button]:transition-colors ' +
  '[&_button:focus-visible]:outline-none [&_button:focus-visible]:ring-2 ' +
  '[&_button:focus-visible]:ring-primary-foreground/60 ' +
  // primary emphasis
  '[&_button.is-primary]:border-transparent [&_button.is-primary]:bg-primary-foreground ' +
  '[&_button.is-primary]:font-semibold [&_button.is-primary]:text-primary ' +
  '[&_button.is-primary:hover]:bg-primary-foreground/90';

/**
 * A quiet group label + hairline divider that clusters related sections
 * (Inspection / Report / Ownership / Procedure) so the page reads as grouped
 * stages rather than a flat stack of independent cards. Deliberately subtle —
 * small uppercase muted text — so it organises without adding visual weight.
 */
export function GroupLabel({ icon, title }: { icon: IconDefinition; title: string }) {
  return (
    <div className="flex items-center gap-2.5 px-1 pt-1">
      <FontAwesomeIcon icon={icon} className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </span>
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
    </div>
  );
}

export function SectionHeader({
  icon,
  title,
  meta,
  actions,
}: {
  icon: IconDefinition;
  title: string;
  /** Optional right-aligned metadata text (e.g. a count) rendered on the bar. */
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-[52px] items-center justify-between gap-3 rounded-t-lg bg-primary px-5 py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <FontAwesomeIcon icon={icon} className="h-4 w-4 shrink-0 text-primary-foreground/85" />
        <h2 className="truncate text-sm font-semibold text-primary-foreground">{title}</h2>
      </div>
      {(meta || actions) && (
        <div className={`flex shrink-0 items-center gap-3 ${HEADER_ACTION_STYLES}`}>
          {meta && <span className="text-xs font-medium text-primary-foreground/80">{meta}</span>}
          {actions}
        </div>
      )}
    </div>
  );
}

export function SectionShell({
  icon,
  title,
  meta,
  actions,
  children,
  bodyClassName = 'px-5 py-4',
}: {
  icon: IconDefinition;
  title: string;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      <SectionHeader icon={icon} title={title} meta={meta} actions={actions} />
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}
